import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * ตรวจสอบว่าวันที่และเวลาที่เลือกชนกับน้องที่ยืนยันคิวไปแล้วหรือไม่
 * @param {string} girlId - ID ของน้อง
 * @param {string} selectedDate - วันที่ที่เลือก (YYYY-MM-DD)
 * @param {string} selectedTime - เวลาที่เลือก (HH:MM)
 * @param {number} duration - ระยะเวลา (ชั่วโมง)
 * @param {string} excludeBookingId - ID ของ booking ที่จะไม่นับ (สำหรับการแก้ไข)
 * @returns {Promise<{isAvailable: boolean, conflictingBookings: Array, message: string}>}
 */
export const validateBookingAvailability = async (girlId, selectedDate, selectedTime, duration, excludeBookingId = null) => {
  try {
    console.log('🔍 Validating booking availability:', {
      girlId,
      selectedDate,
      selectedTime,
      duration,
      excludeBookingId
    });

    // ตรวจสอบวันที่ที่น้องไม่รับงานก่อน
    const girlRef = doc(db, 'girls', girlId);
    const girlSnap = await getDoc(girlRef);
    
    if (girlSnap.exists()) {
      const girlData = girlSnap.data();
      
      // ตรวจสอบสถานะการรับงาน
      if (girlData.status === 'unavailable') {
        return {
          isAvailable: false,
          conflictingBookings: [],
          message: 'น้องปิดรับงานชั่วคราว'
        };
      }
      
      // ตรวจสอบวันที่ไม่รับงาน
      if (girlData.unavailableDates && girlData.unavailableDates.length > 0) {
        const selectedDateObj = new Date(selectedDate);
        const isUnavailableDate = girlData.unavailableDates.some(unavailableDate => {
          let unavailableDateObj;
          
          if (typeof unavailableDate === 'object' && unavailableDate.date) {
            // ถ้าเป็น object ที่มี date property
            unavailableDateObj = new Date(unavailableDate.date);
          } else if (typeof unavailableDate === 'string') {
            // ถ้าเป็น string
            unavailableDateObj = new Date(unavailableDate);
          } else {
            return false;
          }
          
          // เปรียบเทียบวันที่ (ไม่รวมเวลา)
          return unavailableDateObj.getFullYear() === selectedDateObj.getFullYear() &&
                 unavailableDateObj.getMonth() === selectedDateObj.getMonth() &&
                 unavailableDateObj.getDate() === selectedDateObj.getDate();
        });
        
        if (isUnavailableDate) {
          return {
            isAvailable: false,
            conflictingBookings: [],
            message: `วันที่ ${new Date(selectedDate).toLocaleDateString('th-TH')} น้องไม่รับงาน`
          };
        }
      }
    }

    // คำนวณช่วงเวลาที่ต้องการจอง
    const startTime = selectedTime;
    const endTime = calculateEndTime(selectedTime, duration);
    
    console.log('📅 Booking time range:', {
      startTime,
      endTime,
      duration
    });

    // ดึงข้อมูลการจองที่ยืนยันแล้วของน้องคนนี้
    const bookingsRef = collection(db, 'bookings');
    const q = query(
      bookingsRef,
      where('girlId', '==', girlId),
      where('status', 'in', ['confirmed', 'completed'])
    );
    
    const querySnapshot = await getDocs(q);
    const conflictingBookings = [];
    
    querySnapshot.forEach((doc) => {
      const bookingData = doc.data();
      
      // ข้าม booking ที่กำลังแก้ไข
      if (excludeBookingId && doc.id === excludeBookingId) {
        return;
      }
      
      // ตรวจสอบว่าวันที่ตรงกันหรือไม่
      if (bookingData.bookingDate === selectedDate) {
        const existingStartTime = bookingData.bookingTime;
        const existingEndTime = calculateEndTime(bookingData.bookingTime, bookingData.duration || 1);
        
        console.log('🔍 Checking existing booking:', {
          bookingId: doc.id,
          existingStartTime,
          existingEndTime,
          newStartTime: startTime,
          newEndTime: endTime
        });
        
        // ตรวจสอบการชนกันของเวลา
        if (isTimeConflict(startTime, endTime, existingStartTime, existingEndTime)) {
          conflictingBookings.push({
            id: doc.id,
            bookingDate: bookingData.bookingDate,
            bookingTime: bookingData.bookingTime,
            duration: bookingData.duration || 1,
            status: bookingData.status,
            userName: bookingData.userName || 'ไม่ระบุ',
            userEmail: bookingData.userEmail || 'ไม่ระบุ'
          });
        }
      }
    });
    
    const isAvailable = conflictingBookings.length === 0;
    const message = isAvailable 
      ? 'เวลาที่เลือกว่าง สามารถจองได้'
      : `เวลาที่เลือกชนกับน้องที่ยืนยันคิวไปแล้ว (${conflictingBookings.length} การจอง)`;
    
    console.log('✅ Validation result:', {
      isAvailable,
      conflictingBookings: conflictingBookings.length,
      message
    });
    
    return {
      isAvailable,
      conflictingBookings,
      message
    };
    
  } catch (error) {
    console.error('❌ Error validating booking availability:', error);
    return {
      isAvailable: false,
      conflictingBookings: [],
      message: 'เกิดข้อผิดพลาดในการตรวจสอบความพร้อมใช้งาน'
    };
  }
};

/**
 * คำนวณเวลาสิ้นสุดจากการเริ่มต้นและระยะเวลา
 * @param {string} startTime - เวลาเริ่มต้น (HH:MM)
 * @param {number} duration - ระยะเวลา (ชั่วโมง)
 * @returns {string} เวลาสิ้นสุด (HH:MM)
 */
const calculateEndTime = (startTime, duration) => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + (duration * 60);
  const endHours = Math.floor(totalMinutes / 60);
  const endMinutes = totalMinutes % 60;
  
  return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
};

/**
 * ตรวจสอบว่าช่วงเวลาสองช่วงชนกันหรือไม่
 * @param {string} start1 - เวลาเริ่มต้นช่วงที่ 1
 * @param {string} end1 - เวลาสิ้นสุดช่วงที่ 1
 * @param {string} start2 - เวลาเริ่มต้นช่วงที่ 2
 * @param {string} end2 - เวลาสิ้นสุดช่วงที่ 2
 * @returns {boolean} true ถ้าชนกัน
 */
const isTimeConflict = (start1, end1, start2, end2) => {
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  const start1Min = timeToMinutes(start1);
  const end1Min = timeToMinutes(end1);
  const start2Min = timeToMinutes(start2);
  const end2Min = timeToMinutes(end2);
  
  // ชนกันถ้า: start1 < end2 && start2 < end1
  return start1Min < end2Min && start2Min < end1Min;
};

/**
 * ดึงข้อมูลการจองที่ยืนยันแล้วของน้องในวันที่กำหนด
 * @param {string} girlId - ID ของน้อง
 * @param {string} date - วันที่ (YYYY-MM-DD)
 * @returns {Promise<Array>} รายการการจองที่ยืนยันแล้ว
 */
export const getConfirmedBookingsForDate = async (girlId, date) => {
  try {
    const bookingsRef = collection(db, 'bookings');
    const q = query(
      bookingsRef,
      where('girlId', '==', girlId),
      where('bookingDate', '==', date),
      where('status', 'in', ['confirmed', 'completed'])
    );
    
    const querySnapshot = await getDocs(q);
    const bookings = [];
    
    querySnapshot.forEach((doc) => {
      const bookingData = doc.data();
      bookings.push({
        id: doc.id,
        ...bookingData
      });
    });
    
    return bookings;
  } catch (error) {
    console.error('❌ Error getting confirmed bookings:', error);
    return [];
  }
};

/**
 * ดึงข้อมูลการจองที่ยืนยันแล้วของน้องทั้งหมด
 * @param {string} girlId - ID ของน้อง
 * @returns {Promise<Array>} รายการการจองที่ยืนยันแล้วทั้งหมด
 */
export const getAllConfirmedBookings = async (girlId) => {
  try {
    const bookingsRef = collection(db, 'bookings');
    const q = query(
      bookingsRef,
      where('girlId', '==', girlId),
      where('status', 'in', ['confirmed', 'completed'])
    );
    
    const querySnapshot = await getDocs(q);
    const bookings = [];
    
    querySnapshot.forEach((doc) => {
      const bookingData = doc.data();
      bookings.push({
        id: doc.id,
        ...bookingData
      });
    });
    
    return bookings;
  } catch (error) {
    console.error('❌ Error getting all confirmed bookings:', error);
    return [];
  }
};

/**
 * ดึงข้อมูลวันที่ไม่รับงานของน้อง
 * @param {string} girlId - ID ของน้อง
 * @returns {Promise<Array>} รายการวันที่ไม่รับงาน
 */
export const getGirlUnavailableDates = async (girlId) => {
  try {
    const girlRef = doc(db, 'girls', girlId);
    const girlSnap = await getDoc(girlRef);
    
    if (girlSnap.exists()) {
      const girlData = girlSnap.data();
      const unavailableDates = girlData.unavailableDates || [];
      
      // แปลงวันที่เป็น local format (YYYY-MM-DD)
      return unavailableDates.map(date => {
        if (typeof date === 'object' && date.date) {
          const dateObj = new Date(date.date);
          return {
            ...date,
            dateString: `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`
          };
        } else if (typeof date === 'string') {
          const dateObj = new Date(date);
          return {
            date: date,
            dateString: `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`
          };
        }
        return date;
      });
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error getting girl unavailable dates:', error);
    return [];
  }
};

/**
 * ตรวจสอบว่าน้องรับงานในวันที่กำหนดหรือไม่
 * @param {string} girlId - ID ของน้อง
 * @param {string} date - วันที่ (YYYY-MM-DD)
 * @returns {Promise<{isAvailable: boolean, reason: string}>}
 */
export const checkGirlAvailabilityForDate = async (girlId, date) => {
  try {
    const girlRef = doc(db, 'girls', girlId);
    const girlSnap = await getDoc(girlRef);
    
    if (!girlSnap.exists()) {
      return {
        isAvailable: false,
        reason: 'ไม่พบข้อมูลน้อง'
      };
    }
    
    const girlData = girlSnap.data();
    
    // ตรวจสอบสถานะการรับงาน
    if (girlData.status === 'unavailable') {
      return {
        isAvailable: false,
        reason: 'น้องปิดรับงานชั่วคราว'
      };
    }
    
    // ตรวจสอบวันที่ไม่รับงาน
    if (girlData.unavailableDates && girlData.unavailableDates.length > 0) {
      const isUnavailableDate = girlData.unavailableDates.some(unavailableDate => {
        if (typeof unavailableDate === 'object' && unavailableDate.dateString) {
          // ใช้ dateString ที่แปลงแล้วจาก getGirlUnavailableDates
          return unavailableDate.dateString === date;
        } else if (typeof unavailableDate === 'object' && unavailableDate.date) {
          // fallback สำหรับข้อมูลเก่า
          const unavailableDateObj = new Date(unavailableDate.date);
          const unavailableDateStr = `${unavailableDateObj.getFullYear()}-${String(unavailableDateObj.getMonth() + 1).padStart(2, '0')}-${String(unavailableDateObj.getDate()).padStart(2, '0')}`;
          return unavailableDateStr === date;
        } else if (typeof unavailableDate === 'string') {
          // fallback สำหรับข้อมูลเก่า
          const unavailableDateObj = new Date(unavailableDate);
          const unavailableDateStr = `${unavailableDateObj.getFullYear()}-${String(unavailableDateObj.getMonth() + 1).padStart(2, '0')}-${String(unavailableDateObj.getDate()).padStart(2, '0')}`;
          return unavailableDateStr === date;
        }
        return false;
      });
      
      if (isUnavailableDate) {
        // แปลงวันที่เป็นรูปแบบไทย
        const dateObj = new Date(date);
        const thaiDate = `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear() + 543}`;
        return {
          isAvailable: false,
          reason: `วันที่ ${thaiDate} น้องไม่รับงาน`
        };
      }
    }
    
    return {
      isAvailable: true,
      reason: 'น้องพร้อมรับงาน'
    };
    
  } catch (error) {
    console.error('❌ Error checking girl availability:', error);
    return {
      isAvailable: false,
      reason: 'เกิดข้อผิดพลาดในการตรวจสอบความพร้อมใช้งาน'
    };
  }
};
